import React, { isValidElement, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare, faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { useAppDispatch } from '../../../Redux/store'
import { setSideBarOpen } from '../../../Redux/State/App'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

export interface SidebarItemProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    title: string,
    link?: string,
    exact?: boolean,
    sub?: boolean,
    bottom?: boolean,
    targetBlank?: boolean,
    icon?: IconProp
}

const SidebarItem: React.FC<SidebarItemProps> = ({
    children,
    title,
    link,
    exact = false,
    sub = false,
    bottom = false,
    targetBlank = false,
    icon,
    ...restProps
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [childCount, setChildCount] = useState(0);

    const cleanPath = (path: string) => path?.split('#')[0].split('?')[0];
    const currentPath = cleanPath(location.pathname);

    const hasActiveChild = (children: React.ReactNode, currentPath: string): boolean => {
        return React.Children.toArray(children).some(child => {
            if (isValidElement(child)) {
                const { link, exact, children: grandChildren } = child.props;

                const cleanPath = (path: string) => path?.split('#')[0].split('?')[0];
                const childLink = cleanPath(link);
                const isActive = link
                    ? exact
                        ? currentPath === childLink
                        : currentPath.includes(childLink)
                    : false;

                // Check self or recursively check grandchildren
                return isActive || hasActiveChild(grandChildren, currentPath);
            }
            return false;
        });
    };

    const childActive = hasActiveChild(children, currentPath);

    const active = link
        ? exact
            ? currentPath === cleanPath(link)
            : currentPath.includes(cleanPath(link))
        : false;

    const isActive = active || childActive;

    const handleClick = () => {
        if (!link) return;

        if (targetBlank) {
            window.open(link, '_blank', 'noopener,noreferrer');
        } else {
            navigate(link);
            dispatch(setSideBarOpen(false));
        }
    };

    useEffect(() => {
        const count = React.Children.count(children);
        setChildCount(count);
    }, [children]);

    return (
        <div {...restProps} className={`mx-2 ${bottom ? 'mt-auto' : ''}`}>
            <motion.button
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={handleClick}
                type="button"
                className={`
                    relative flex items-center w-full px-1 rounded-full transition-all duration-200 group
                    ${sub ? 'opacity-90 hover:opacity-100 max-w-[100%] py-1' : 'py-2 my-1'}
                    ${active
                        ? `text-brand font-medium cursor-default`
                        : 'text-gray-700'}
                `}
            >
                <div className="ml-2 flex items-center gap-3 w-full">
                    {icon && (
                        <div className="flex items-center justify-center w-5 h-5 ml-2">
                            <FontAwesomeIcon
                                icon={icon}
                                className={`
                                    transition-all duration-200
                                    ${active ? 'text-brand scale-[1.2]' : 'text-gray-500 group-hover:scale-[1.2] group-focus:scale-[1.2]'}
                                `}
                            />
                        </div>
                    )}

                    <span className={`relative line-clamp-1 transition-all duration-200 ${active ? 'font-medium' : ''}`}>
                        {title}
                        {targetBlank && <FontAwesomeIcon className='text-[10px] ml-1 mb-2' icon={faArrowUpRightFromSquare} />}
                    </span>


                    {childCount > 0 && (
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`
                                ml-auto mr-2 text-xs transition-transform duration-200
                                ${isActive ? 'rotate-180' : ''}
                                ${active ? 'text-brand' : 'text-gray-400 group-hover:text-gray-500'}
                            `}
                        />
                    )}
                </div>
            </motion.button>

            <AnimatePresence>
                {children && isActive && (
                    <motion.div
                        ref={dropdownRef}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{
                            height: 39 * childCount,
                            opacity: 1
                        }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden flex flex-col gap-2"
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30
                        }}
                    >
                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SidebarItem;
